import { CheckCircle2, Circle, Loader2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ProcessingStep {
  id: string;
  label: string;
  status: "pending" | "active" | "completed" | "error";
  phase: "review" | "generate";
}

export interface ProcessingStepListProps {
  steps: ProcessingStep[];
}

export function ProcessingStepList({ steps }: ProcessingStepListProps) {
  const hasError = steps.some(step => step.status === "error");
  const allCompleted = steps.every(step => step.status === "completed");
  
  // Group steps by phase
  const reviewSteps = steps.filter(s => s.phase === "review");
  const generateSteps = steps.filter(s => s.phase === "generate");
  
  // Determine current phase
  const isReviewPhase = reviewSteps.some(s => s.status === "active" || s.status === "pending");
  const isGeneratePhase = generateSteps.some(s => s.status === "active");

  const renderStep = (step: ProcessingStep) => (
    <div key={step.id} className="flex items-center gap-3">
      <div className="flex-shrink-0">
        {step.status === "completed" && (
          <CheckCircle2 className="h-5 w-5 text-primary" />
        )}
        {step.status === "active" && (
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
        )}
        {step.status === "pending" && (
          <Circle className="h-5 w-5 text-muted-foreground/40" />
        )}
        {step.status === "error" && (
          <XCircle className="h-5 w-5 text-destructive" />
        )}
      </div>
      <span
        className={cn(
          "text-sm leading-tight",
          step.status === "active" && "text-foreground font-medium",
          step.status === "completed" && "text-muted-foreground",
          step.status === "pending" && "text-muted-foreground/60",
          step.status === "error" && "text-destructive font-medium"
        )}
      >
        {step.label}
      </span>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Review Phase Steps */}
      <div className="space-y-3 mb-6">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Analysis
        </p>
        <div className="space-y-2.5 pl-1">
          {reviewSteps.map(renderStep)}
        </div>
      </div>

      {/* Generate Phase Steps */}
      <div className="space-y-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Generation
        </p>
        <div className="space-y-2.5 pl-1">
          {generateSteps.map(renderStep)}
        </div>
      </div>
    </div>
  );
}
